// add-value.component.ts
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { IPicklistValuesData } from "../../interfaces/administration/picklistValuesData";
import { PicklistService } from "../../services/administration/picklist/picklist.service";
import { IPicklistFieldsData } from "../../interfaces/administration/picklistFieldsData";
import { ILoggedUserData } from "../../interfaces/common/loggedUserData";
import { LoggedUserService } from "../../services/common/logged-user.service";

@Component({
  selector: "app-edit-value",
  templateUrl: "./edit-value.component.html",
  styles: [
    `
      .button-container {
        display: flex !important;
        justify-content: space-between !important;
        margin: 10px 0px 0px 0px;
      }
    `,
  ],
})
export class EditValueComponent implements OnInit, OnChanges {
  @Input() selectedValue!: IPicklistValuesData;
  @Input() selectedField!: IPicklistFieldsData;
  @Output() valueUpdated = new EventEmitter<IPicklistValuesData>();
  @Output() formClosed = new EventEmitter<void>();

  form: FormGroup;
  isLoading = false;
  validationMessages: string[] = [];
  values: IPicklistValuesData[] = [];

  shouldSeeIsOnPrem: boolean = false;
  currentUser: ILoggedUserData;

  constructor(
    private fb: FormBuilder,
    private pickListService: PicklistService,
    private loggedUserService: LoggedUserService
  ) {
    this.form = this.fb.group({
      valueName: ["", Validators.required],
      id: [0],
      parentPicklist: [""],
      isOnPrem: [false],
    });
  }

  ngOnInit(): void {
    if (this.selectedValue) {
      this.initializeComponent();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if selectedValue has changed
    if (changes["selectedValue"] && !changes["selectedValue"].firstChange) {
      this.initializeComponent();
    }
  }

  private initializeComponent(): void {
    this.loggedUserService.getLoggedUserData().subscribe((data) => {
      this.currentUser = data;
    });

    // Reset the form to default state first
    this.form.reset({
      valueName: "",
      id: 0,
      parentPicklist: 0,
      isOnPrem: false,
    });

    // Store the parent ID for later use
    const parentId = this.selectedValue.parentId;

    // Then patch with the new values - only do this once, not inside the subscription
    this.form.patchValue({
      valueName: this.selectedValue.value,
      id: this.selectedValue.id,
      isOnPrem: this.selectedValue.isOnPrem || false, // Ensure it's not undefined
    });

    // If this field has a parent field, load the parent values
    if (this.selectedField?.parentPicklistFieldsId != null) {
      this.pickListService
        .getPicklistValues(this.selectedField.parentPicklistFieldsId)
        .subscribe((data) => {
          if (data) {
            this.values = data;

            // Now that we have the parent values loaded, set the parent selection
            // This needs to happen AFTER we load the values, otherwise the dropdown won't have options
            setTimeout(() => {
              this.form.patchValue({
                parentPicklist: parentId,
              });
            });

            this.shouldSeeIsOnPrem =
              this.selectedField.name.includes("School Attended");
          }
        });
    } else {
      this.shouldSeeIsOnPrem =
        this.selectedField.name.includes("School Attended");
    }
  }

  onSubmit() {
    if (this.form.invalid) {
      this.validationMessages.push("Value is required.");
      return;
    }

    this.isLoading = true;
    this.validationMessages = [];
    const val: IPicklistValuesData = {
      id: this.form.value.id,
      value: this.form.value.valueName,
      isOnPrem: this.form.value.isOnPrem,
      picklistFieldId: this.selectedValue.picklistFieldId,
      parentId: this.form.value.parentPicklist,
      facilityId: Number(this.currentUser?.facilityId) || 1,
    };
    this.valueUpdated.emit(val);
    this.form.reset();
    this.isLoading = false;
  }

  onIsOnPremChange(event: any) {
    // Make sure we're setting the value properly and marking the form as dirty
    const isChecked = event.target.checked;
    this.form.get("isOnPrem")?.setValue(isChecked);
    this.form.get("isOnPrem")?.markAsDirty();
    this.form.markAsDirty();
  }

  cancel() {
    this.formClosed.emit();
  }
}
